import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {CSSTransition} from "react-transition-group";
import {useDispatch, useSelector} from "react-redux";
import {toast} from 'react-toastify';

import styles from './ShopReviewOrder.module.scss';

import ButtonMain from "../../components/buttons/ButtonMain/ButtonMain";
import {
    ADYEN_RETURN_URL,
    FREE_TEXT,
    TAX_INCLUDED_TEXT,
    CONFIG_PAYMENT
} from "../../shared/constants";
import ButtonProceed from "../../components/buttons/ButtonProceed/ButtonProceed";
import LoadableImage from "../../components/LoadableImage/LoadableImage";
import {validateEmail} from "../../shared/helpers";
import {clearSelectedOffer} from "../../redux/rootReducer";
import withRequestHandler from "../../HOC/withRequestHandler";
import Loading from "../../components/Loading/Loading";
import PopupInfo from "../../components/PopupInfo/PopupInfo";
import PopupAgeConfirm from "../../components/PopupAgeConfirm/PopupAgeConfirm";

const ShopReviewOrder = ({
    itemImg,
    priceFormatted,
    name,
    config,
    windowSize,
    isFree,
    makeRequest,
    isLoading
}) => {
    let isFirstPageView = useRef(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const inputEmailRef = useRef(null);
    const inputEmailFieldRef = useRef(null);
    const [isAgreed, setIsAgreed] = useState(false);
    const [inputEmail, setInputEmail] = useState('');
    const [emailError, setEmailError] = useState(null);
    const [savedMail, setSavedMail] = useState(null);
    const [newMailEntered, setNewMailEntered] = useState(false);
    const [showAgeConfirm, setShowAgeConfirm] = useState(false);
    const { error, selectedOffer, paymentProvider } = useSelector(state => state.rootReducer);

    const unselectOffer = () => {
        dispatch(clearSelectedOffer());
    };

    const updateInputValue = (value) => {
        if (!newMailEntered
            && savedMail
            && (value !== savedMail)
        ){
            setNewMailEntered(true);
        }
        
        setInputEmail(value);
    };

    const toggleSaveUserEmail = (email) => {
        if (!email || !email.length || !validateEmail(email)) {
            toast.error(config.errors.MAIL_INVALID);
            return;
        }

        const player = JSON.parse(localStorage.getItem('shishiPlayerInfo'));
        if (!savedMail || (savedMail && newMailEntered)) {
            player.savedEmail = email;
            localStorage.setItem('shishiPlayerInfo', JSON.stringify(player));
            toast.success(config.successfulSavedMail);
            setSavedMail(email);
            setNewMailEntered(false);
        }
        
        if (savedMail && !newMailEntered) {
            delete player.savedEmail;
            localStorage.setItem('shishiPlayerInfo', JSON.stringify(player));
            toast.success(config.successfulUnsavedMail);
            setSavedMail(null);
        }
    };

    const agreeAndEnableProceed = () => {
        if (!isAgreed && !isFree) {
            inputEmailFieldRef.current.focus();
            inputEmailRef.current.scrollIntoView();
        }
        setIsAgreed(!isAgreed);
    };

    /**
     * Validates the email and if it's valid, shows the age confirmation popup.
     * If the email is not valid, it sets the email error
     */
    const proceed = () => {
        if (!inputEmail.trim().length) {
            setEmailError(config.errors.MAIL_EMPTY);
            return;
        }

        const verifiedEmail = validateEmail(inputEmail);
        if (verifiedEmail) {
            // Show age confirmation popup before proceeding to payment
            setShowAgeConfirm(true);
        } else {
            setEmailError(config.errors.MAIL_INVALID);
        }
    };

    /**
     * Called when user confirms they are 18+ in the age popup.
     * Proceeds to the payment page based on payment provider.
     */
    const handleAgeConfirm = () => {
        setShowAgeConfirm(false);
        
        // Check for the payment provider, because there is different flow for the different providers
        if (paymentProvider === CONFIG_PAYMENT.paymentProviders.adyen) {
            navigate('/checkout', {state: {email: inputEmail}});
        } else {
            const newTab = window.open('about:blank', '_blank');
            if (newTab) {
                newTab.document.write(`<img src="/loading_default_circle.gif" alt="Loading..." id="loadingPayment" style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);" />`);
                newTab.document.close();
            } else {
                // Popup blocked or failed to open
                alert(config.newTabRestrictions);
            }

            startPurchase(selectedOffer, newTab);
        }
    };

    /**
     * Called when user cancels the age confirmation popup.
     * Goes back to shop home.
     */
    const handleAgeCancel = () => {
        setShowAgeConfirm(false);
        dispatch(clearSelectedOffer());
        navigate('/shop');
    };

    /**
     * Start the purchase process
     * Gets URL from the response and opens it in new tab.
     * @param selectedOffer {Object}
     * @param newTab {Window}
     * @returns {Promise<void>}
     */
    const startPurchase = async (selectedOffer, newTab) => {
        let player = localStorage.getItem('shishiPlayerInfo');
        player = JSON.parse(player);

        const response = await makeRequest('post', 'startPurchase', {
            redirectUrl: ADYEN_RETURN_URL,
            username: player.username,
            userId: player.userId,
            email: inputEmail,
            pack: selectedOffer
        });

        if (!response || !response.redirectPage || !response.canShop) {
            if (newTab && !newTab.closed) {
                newTab.close();
            }
            dispatch(clearSelectedOffer());
            navigate('/checkout-result', { state: {success: false} });
            return;
        } else {
            // Check if needed and add query params to the URL
            // like response.orderId / player information etc.
            if (CONFIG_PAYMENT.isOpeningInNewTab) {
                newTab.location.href = response.redirectPage;
                navigate('/checkout-result', { state: {success: true, isGeneric: true, transactionId: response.intentionId} });
            } else {
                navigate('/checkout-hub', {state: {url: response.redirectPage}});
            }
        }
    };

    const proceedFree = async () => {
        let player = localStorage.getItem('shishiPlayerInfo');
        player = JSON.parse(player);

        const response = await makeRequest('post', 'claimFree', {
            redirectUrl: ADYEN_RETURN_URL,
            username: player.username,
            userId: player.userId,
            pack: selectedOffer
        });

        if (!response || !response.intentionId) {
            dispatch(clearSelectedOffer());
            navigate('/checkout-result', { state: {success: false} });
        } else {
            dispatch(clearSelectedOffer());
            navigate('/checkout-result', {state: {success: true, transactionId: response.intentionId}});
        }
    };

    const handleGoBack = useCallback((event) => {
        if (event) {
            event.preventDefault();
            unselectOffer();
            navigate('/shop');
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);

        let player = localStorage.getItem('shishiPlayerInfo');
        player = JSON.parse(player);

        setInputEmail(player.savedEmail);
        setSavedMail(player.savedEmail);
    }, []);

    useEffect(() => {
        window.addEventListener('popstate', (event) => handleGoBack(event), false);

        return () => {
            window.removeEventListener('popstate', handleGoBack);
        }
    }, [handleGoBack]);

    useEffect(() => {
        if (isFirstPageView.current) {
            window.gtag('event', 'screen_view', {
                'app_name': 'Shishi shop',
                'screen_name': 'page_purchase_confirmation'
            });
            window.gtag('event', 'page_view', {
                'app_name': 'Shishi shop',
                'page_title': 'checkout'
            });
            isFirstPageView.current = false;
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <CSSTransition in appear timeout={300} classNames={'fade-in-slide-down'}>
                <div className={styles.title}>{config.reviewOrder}</div>
            </CSSTransition>
            <div className={'dividerHorizontal'}></div>
            <div className={styles.offerInfo}>
                {/* <img className={styles.offerInfoBackground} src={OfferInfoBackground} alt={''}/> */}
                <div className={styles.offerImgWrapper}>
                    {/* <img className={styles.offerImgBackground} src={OfferImgBackground} alt={''} /> */}
                    <div className={styles.offerImg}>
                        <LoadableImage img={itemImg} />
                    </div>
                </div>
                <div className={styles.info}>
                    <div className={styles.offerType}>{config.orderName}{name}</div>
                    <div className={styles.newPrice}>{config.orderPrice}{!isFree ? priceFormatted : FREE_TEXT}{!isFree ? TAX_INCLUDED_TEXT : null}</div>
                    <div className={styles.quantity}>{config.orderQuantity}</div>
                    {windowSize.isMobileView ?
                        <ButtonMain text={config.remove} isSmall={true} onClick={() => {unselectOffer()}} isNegative={true} />
                    :
                        null
                    }
                </div>
                {!windowSize.isMobileView ?
                    <div className={styles.buttonRemove}>
                        <ButtonMain
                            text={config.remove}
                            isSmall={true}
                            onClick={unselectOffer}
                            isNegative={true}
                        />
                    </div>
                :
                    null
                }
            </div>
            <div className={'dividerHorizontal'}></div>
            <div className={styles.discountWrapper}>
                <div className={styles.newPrice}>{config.orderPrice}{!isFree ? priceFormatted : FREE_TEXT}{!isFree ? TAX_INCLUDED_TEXT : null}</div>
                <div className={styles.quantity}>{config.orderQuantity}</div>
                <div className={'dividerHorizontal'}></div>
            </div>
            <div className={styles.total}>{config.orderTotal}&nbsp;&nbsp;&nbsp;{!isFree ? priceFormatted : FREE_TEXT}{!isFree ? TAX_INCLUDED_TEXT : null}</div>
            <div className={styles.precautionNotes}>
                <p className={styles.highlight}>購入時のご注意</p>
                <p>- ご購入いただいた商品は、ゲーム内の書簡により送付します。</p>
                <p>- 書簡送付後、7日間以内に商品をお受取りにならない場合、書簡に添付された商品は消滅します。</p>
                <p>- この内容でよければ、「私は利用規約に同意して、上記の商品を購入します」に✔を入れてから「この内容で購入する」を押して購入してください。</p>
                <p>- 修正したい部分があれば「商品の削除」ボタンを押して商品を選びなおしてください。</p>
                <p>- アイテムは、例外を除き、ゲーム内の交換機能による売買を行えません。</p>
                <p>- 商品の価格は、本サイト上でクレジットカード等で決済を行った場合の価格です。</p>
                <p>- ご購入いただいた内容は、ご記入いただいたE-Mailアドレス宛てに、お送りしております。
                    ※ご記入いただいたE-Mailアドレスは、一度だけご購入内容を送付いたします。送付後、弊社ECサイトのデータベースからE-Mailアドレスを削除いたします。
                    ※弊社からのE-Mailが届かない場合は、迷惑メールなどを確認し、それでもない場合はお問い合わせにてご相談ください。
                </p>
            </div>
            {isFree ?
                null
                :
                <div className={styles.emailInputWrapper} ref={inputEmailRef}>
                    <div className={styles.inputLabel}>{config.emailInput}</div>
                    <input
                        placeholder={config.emailInputPlaceholder}
                        onFocus={() => emailError ? setEmailError(null) : null}
                        onChange={(e) => updateInputValue(e.target.value)}
                        value={inputEmail}
                        maxLength={320}
                        ref={inputEmailFieldRef}
                    />
                    {emailError ?
                        <div className={styles.errorEmailInput}>
                            {emailError}
                        </div>
                        :
                        null
                    }
                </div>
            }
            <div className={styles.stickyButtonWrapper}>
            {isFree ?
                null
                :
                <div className={styles.saveMail} onClick={() => toggleSaveUserEmail(inputEmail)}>
                    <input type='checkbox' checked={savedMail && !newMailEntered} readOnly={true} />
                    <span className={styles.saveMailText}>{config.saveMailText}</span>
                </div>
            }
                <div className={styles.agree} onClick={agreeAndEnableProceed}>
                    <input type='checkbox' checked={isAgreed} readOnly={true} />
                    <a target={'_blank'} href={'https://shishi.6waves.jp/rule.html'} className={styles.terms}><span>{config.agreeWithTermsAndCond}</span></a>
                </div>
                <div className={styles.buttonWrapper}>
                    <ButtonProceed text={config.proceedToCheckout} isDisabled={!isAgreed} onClick={isFree ? proceedFree : proceed} />
                </div>
            </div>
            {isLoading ? <Loading withOverlay={true} /> : null}
            {error.hasError ? <PopupInfo text={error.message} /> : null}
            {showAgeConfirm ? 
                <PopupAgeConfirm 
                    config={config} 
                    onConfirm={handleAgeConfirm} 
                    onCancel={handleAgeCancel} 
                /> 
                : null
            }
        </div>
    );
};

export default withRequestHandler(ShopReviewOrder);